import type {
  GetCompetitionCashProfitStandingsRow,
  GetCompetitionFairPnlStandingsRow,
  GetCompetitionStandingsRow,
  GetCompetitionsRow,
} from '@morfi/backend-sdk-v2/client';

import type { UserIdentityShape } from '@/components/profile/user-identity';
import { parseAvatarPreferences } from '@/components/social/avatar-preferences';
import {
  COMPETITION_BOARD_COLUMNS,
  type TradingCompetitionBoard,
  type TradingCompetitionMetric,
  type TradingCompetitionStandingRow,
  type TradingCompetitionSummary,
} from '@/components/social/competition/types';

/**
 * SDK rows → the competition display contracts.
 *
 * Standings come back as the unified leaderboard row (every board's fields
 * present, the inactive ones `null`), so the competition's board and metric
 * decide which field is the rank and which are the columns. That choice is made
 * here, once, rather than at each render site — getting it wrong shows a
 * plausible number from the wrong column, which no type would catch.
 */

/** Where each metric's VALUE lives on the unified standings row. */
const VALUE_FIELD: Readonly<
  Record<
    TradingCompetitionBoard,
    Readonly<Partial<Record<TradingCompetitionMetric, keyof GetCompetitionStandingsRow>>>
  >
> = {
  traders: {
    pnl: 'realizedPnl',
    volume: 'totalVolume',
    trades: 'totalTrades',
    markets: 'marketsTraded',
  },
  backers: {
    backed: 'totalBackedNetD6',
    backs: 'numBacks',
    topMarket: 'topMarketVolume',
  },
  creators: {
    totalVolume: 'creatorTotalVolume',
    marketsCreated: 'totalMarketsCreated',
    topMarket: 'topMarketVolume',
  },
  boosters: {
    boostVolume: 'totalAmountBoosted',
    boosts: 'numBoosts',
    marketsBoosted: 'marketsBoosted',
  },
};

/** Where each metric's RANK lives on the unified standings row. */
const RANK_FIELD: Readonly<
  Record<
    TradingCompetitionBoard,
    Readonly<Partial<Record<TradingCompetitionMetric, keyof GetCompetitionStandingsRow>>>
  >
> = {
  traders: {
    pnl: 'pnlRank',
    volume: 'volumeRank',
    trades: 'tradesRank',
    markets: 'marketsRank',
  },
  backers: {
    backed: 'amountRank',
    backs: 'backsRank',
    topMarket: 'topMarketRank',
  },
  creators: {
    totalVolume: 'totalVolumeRank',
    marketsCreated: 'marketsCreatedRank',
    topMarket: 'topMarketRank',
  },
  boosters: {
    boostVolume: 'amountRank',
    boosts: 'boostsRank',
    marketsBoosted: 'marketsBoostedRank',
  },
};

/**
 * The competition summary, or `null` when the row names a metric this build
 * does not know — a competition rendered with an unknown metric would show an
 * empty ranking column, so it is dropped from the list instead.
 */
export function toCompetitionSummary(row: GetCompetitionsRow): TradingCompetitionSummary | null {
  const metric = toMetric(row.board, row.metric);
  if (metric === null) return null;
  return {
    id: row.id,
    title: row.title,
    subtitle: row.subtitle,
    board: row.board,
    metric,
    phase: row.phase,
    start: row.start,
    end: row.end,
    bannerImageUrl: row.bannerImageUrl,
    prizePoolD6: row.prizePoolD6,
    rankedParticipants: row.rankedParticipants,
    rankingSource: row.rankingSource,
  };
}

export function toCompetitionSummaries(
  rows: ReadonlyArray<GetCompetitionsRow>,
): readonly TradingCompetitionSummary[] {
  const out: TradingCompetitionSummary[] = [];
  for (const row of rows) {
    const summary = toCompetitionSummary(row);
    if (summary !== null) out.push(summary);
  }
  return out;
}

/**
 * One standings row for `competition`. `null` when the row carries no rank on
 * the competition's metric: ranks come from the board and are never synthesised
 * from list position, so an unranked row has nothing to show.
 */
export function toStandingRow(
  row: GetCompetitionStandingsRow,
  competition: Pick<TradingCompetitionSummary, 'board' | 'metric'>,
): TradingCompetitionStandingRow | null {
  const rankField = RANK_FIELD[competition.board][competition.metric];
  if (rankField === undefined) return null;
  const ranked = row[rankField];
  if (typeof ranked !== 'number' || !Number.isFinite(ranked)) return null;
  return {
    rank: ranked,
    user: toIdentity(row.user),
    values: readValues(row, competition.board),
    prizeD6: typeof row.prizeD6 === 'string' ? row.prizeD6 : null,
  };
}

export function toStandingRows(
  rows: ReadonlyArray<GetCompetitionStandingsRow>,
  competition: Pick<TradingCompetitionSummary, 'board' | 'metric'>,
): readonly TradingCompetitionStandingRow[] {
  const out: TradingCompetitionStandingRow[] = [];
  for (const row of rows) {
    const mapped = toStandingRow(row, competition);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}

export function toFairPnlStandingRows(
  rows: ReadonlyArray<GetCompetitionFairPnlStandingsRow>,
): readonly TradingCompetitionStandingRow[] {
  return rows.map((row) => ({
    rank: row.rank,
    user: toIdentity(row.user),
    values: {
      fairPnl: row.fairPnlD6,
      realizedPnl: row.realizedPnlD6,
      boostsPaid: row.boostsD6,
      pumpProfit: row.pumpProfitD6,
    },
    prizeD6: row.prizeD6,
  }));
}

export function toCashProfitStandingRows(
  rows: ReadonlyArray<GetCompetitionCashProfitStandingsRow>,
): readonly TradingCompetitionStandingRow[] {
  return rows.map((row) => ({
    rank: row.rank,
    user: toIdentity(row.user),
    values: {
      cashProfit: row.cashProfitD6,
      cashIn: row.cashInD6,
      cashOut: row.cashOutD6,
      transfers: row.transfersD6,
    },
    prizeD6: row.prizeD6,
  }));
}

// ── Internals ───────────────────────────────────────────────────────────────

/** Every column the board renders, so a row is complete even off the ranking metric. */
function readValues(
  row: GetCompetitionStandingsRow,
  board: TradingCompetitionBoard,
): Readonly<Partial<Record<TradingCompetitionMetric, string | number>>> {
  const values: Partial<Record<TradingCompetitionMetric, string | number>> = {};
  for (const column of COMPETITION_BOARD_COLUMNS[board]) {
    const field = VALUE_FIELD[board][column.metric];
    if (field === undefined) continue;
    const value = row[field];
    if (typeof value === 'string' || typeof value === 'number') values[column.metric] = value;
  }
  return values;
}

function toMetric(board: TradingCompetitionBoard, metric: string): TradingCompetitionMetric | null {
  const column = COMPETITION_BOARD_COLUMNS[board].find((entry) => entry.metric === metric);
  return column?.metric ?? null;
}

type CompetitionSdkUser =
  | GetCompetitionStandingsRow['user']
  | GetCompetitionFairPnlStandingsRow['user']
  | GetCompetitionCashProfitStandingsRow['user'];

function toIdentity(user: CompetitionSdkUser): UserIdentityShape {
  const raw = (user ?? {}) as Record<string, unknown>;
  const providers = Array.isArray(raw.connectedProviders)
    ? (raw.connectedProviders as Array<Record<string, unknown>>)
    : [];
  return {
    userId: typeof raw.userId === 'string' ? raw.userId : null,
    walletAddress: typeof raw.walletAddress === 'string' ? raw.walletAddress : '',
    displayName: typeof raw.displayName === 'string' ? raw.displayName : null,
    handle: typeof raw.handle === 'string' ? raw.handle : null,
    avatarUrl: typeof raw.avatarUrl === 'string' ? raw.avatarUrl : null,
    avatarNsfw: raw.avatarNsfw === true,
    avatarPreferences: parseAvatarPreferences(raw.avatarPreferences),
    connectedProviders: providers
      .filter(
        (provider): provider is { provider: 'x' | 'google' | 'apple' } & Record<string, unknown> =>
          provider.provider === 'x' ||
          provider.provider === 'google' ||
          provider.provider === 'apple',
      )
      .map((provider) => ({
        provider: provider.provider,
        username: typeof provider.username === 'string' ? provider.username : '',
        displayName: typeof provider.displayName === 'string' ? provider.displayName : null,
      })),
  };
}
